import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { hasValue } from 'utils/hasValue';

import { API_URL } from 'constants/environment';
import { useAuthContext } from 'contexts/AuthProvider';

import { setCredentials } from 'reduxLocal/slices/authSlice';

import { useRedirectTo } from 'hooks/useRedirectTo';

const EXCHANGE_URL = `${API_URL}/api/v1/authentication/impersonations/exchange`;

export const ImpersonationLogin = () => {
  const { setAccessToken } = useAuthContext();
  const { navigateWithRedirectTo } = useRedirectTo();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const [message, setMessage] = useState<string>('Starting impersonation…');

  useEffect(() => {
    const run = async () => {
      try {
        const code = searchParams.get('code') ?? null;
        const orgId = searchParams.get('org_id') ?? null;

        const redirectTo =
          searchParams.get('redirect_to') ||
          `/${orgId ? `org/${orgId}` : 'login_transition'}`;

        if (!hasValue(code)) {
          setMessage('Missing impersonation code.');
          return;
        }

        const resp = await fetch(EXCHANGE_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            accept: 'application/json'
          },
          body: JSON.stringify({ code }),
          credentials: 'include'
        });

        if (!resp.ok) {
          setMessage(`Impersonation failed (${resp.status}).`);
          return;
        }

        const { access_token: accessToken } = await resp.json();

        if (!accessToken) {
          setMessage('Exchange succeeded but no access token returned.');
          return;
        }

        setAccessToken(accessToken);
        dispatch(setCredentials({ access_token: accessToken }));
        localStorage.setItem('isLoggedIn', 'true');

        setMessage('Impersonation successful. Redirecting…');
        navigateWithRedirectTo(redirectTo, {
          state: { loggingIn: true }
        });
      } catch (e: any) {
        const backendMessage = e?.message || String(e);

        setMessage(`Unexpected error: ${backendMessage}`);
      }
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <div className="text-red-700">Impersonation</div>
      <div className="text-red-700">{message}</div>
    </div>
  );
};
